import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import ManageUserDeletionImage from './ManageUserDeletionImage'

const copy = {
  overline: `Right to erasure`,
  heading: `Manage user deletion across Segment and supported Destinations`,
}

const boxes = [
  {
    headline: `Delete user data from Segment`,
    content: `Issue deletion requests from a simple interface or use Segment’s HTTP API to delete a specified user’s data from Segment.`,
  },
  {
    headline: `Forward deletion requests`,
    content: `Automatically delete user data from supported raw data Destinations or send deletion requests to compatible tools, like Mixpanel, Braze, and Intercom.`,
  },
  {
    headline: `Monitor deletion status and requests`,
    content: `Get visibility into the progress of deletion requests to confirm when data is deleted, so you can update your users and your company.`,
  },
]

const ManageUserDeletion: FC<PropsWithChildren<unknown>> = () => {
  return (
    <SectionBase variant="2Col" copyVariant="narrow" sticky={true}>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Overline tag="h3" variant="h5">
            {copy.overline}
          </CopyText.Overline>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
        </CopyTextBase>
        <ColumnTrackWrapper>
          <ColumnTrack gap="medium">
            {boxes.map(({ headline, content }) => (
              <FeatureBoxBase alignment="left" key={headline}>
                <FeatureBox.Body>
                  <FeatureBox.Title>{headline}</FeatureBox.Title>
                  <FeatureBox.Text>{content}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </ColumnTrackWrapper>
      </Section.Copy>
      <Section.Contents>
        <Section.Sticky>
          <ImageWrapper
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              transition: {
                duration: 0.4,
                delay: 0.1,
              },
            }}
            viewport={{ once: true }}
          >
            <ManageUserDeletionImage />
          </ImageWrapper>
        </Section.Sticky>
      </Section.Contents>
    </SectionBase>
  )
}

export default ManageUserDeletion

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

const ImageWrapper = styled(m.div)``
